import axios from 'axios'
import type { Signal, SignalListResponse, SignalConfig, HealthResponse, AuthStatusResponse, TokenVerifyResponse, MonitoringSettings, DomMode } from '@/types'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'
const AUTH_TOKEN_KEY = 'has_auth_token'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor - handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and trigger page reload to show login
      localStorage.removeItem(AUTH_TOKEN_KEY)
      window.location.reload()
    }
    return Promise.reject(error)
  }
)

// Signal endpoints
export interface SignalFilters {
  symbol?: string
  source?: 'dom' | 'trade'
  status?: 'active' | 'disappeared'
  from_time?: string  // ISO datetime
  to_time?: string    // ISO datetime
  page?: number
  page_size?: number
}

export async function getSignals(filters: SignalFilters = {}): Promise<SignalListResponse> {
  const params = new URLSearchParams()

  if (filters.symbol) params.append('symbol', filters.symbol)
  if (filters.source) params.append('source', filters.source)
  if (filters.status) params.append('status', filters.status)
  if (filters.from_time) params.append('from_time', filters.from_time)
  if (filters.to_time) params.append('to_time', filters.to_time)
  if (filters.page) params.append('page', filters.page.toString())
  if (filters.page_size) params.append('page_size', filters.page_size.toString())

  const response = await api.get<SignalListResponse>(`/signals?${params.toString()}`)
  return response.data
}

export async function getActiveSignals(symbol?: string): Promise<Signal[]> {
  const params = symbol ? `?symbol=${symbol}` : ''
  const response = await api.get<Signal[]>(`/signals/active${params}`)
  return response.data
}

export async function getSignal(id: string): Promise<Signal> {
  const response = await api.get<Signal>(`/signals/${id}`)
  return response.data
}

// Config endpoints
export async function getConfigs(activeOnly = true): Promise<SignalConfig[]> {
  const response = await api.get<SignalConfig[]>(`/configs?active_only=${activeOnly}`)
  return response.data
}

// Monitoring settings endpoints
export async function getMonitoringSettings(): Promise<MonitoringSettings> {
  const response = await api.get<MonitoringSettings>('/settings')
  return response.data
}

export async function updateDomMode(domMode: DomMode): Promise<MonitoringSettings> {
  const response = await api.patch<MonitoringSettings>('/settings', { dom_mode: domMode })
  return response.data
}

// Health endpoint
export async function getHealth(): Promise<HealthResponse> {
  const response = await axios.get<HealthResponse>('/health')
  return response.data
}

// Auth endpoints
export async function getAuthStatus(): Promise<AuthStatusResponse> {
  const response = await api.get<AuthStatusResponse>('/auth/status')
  return response.data
}

export async function verifyToken(token: string): Promise<TokenVerifyResponse> {
  const response = await api.post<TokenVerifyResponse>('/auth/verify', { token })
  return response.data
}

// Auth helpers
export function getStoredToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY)
}

export function setStoredToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token)
}

export function clearStoredToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY)
}

export default api
